/* ============================================
   QUOTE DASHBOARD — Replacement Claim Card
   Asset: dashboard-claim-card.js

   CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-21)

   WHAT THIS IS
   Part of a delivery arrives damaged. Two things then happen on two different
   clocks, and conflating them is how a rep tells a customer the wrong thing:

     FULFILLMENT — "when does my customer get their replacement?"  → the rep's
       question. Answered by the replacement order's own progress bar, which is
       visible to the rep like any other deal.

     THE CLAIM — "did the carrier actually pay us?"  → the company's question.
       Answered by THIS card. It is our money, on our timeline, and the customer
       neither sees it nor should be told about it.

   ⛔ ADMIN-ONLY, and the worker agrees: `claim` is deliberately excluded from the
   `allowed` field list in handlePostQuoteLifecycle and handled by its own
   `rep.admin` branch, because that list is writable by any rep who owns the deal.

   Loaded via <script defer> in sections/rep-quote-dashboard.liquid BEFORE
   main.js. Public API on window.RQD:

     renderClaimCard(q)        -> html string ('' when not applicable)
     renderReplacementStrip(q) -> html string ('' when the deal has no replacement)

   Init contract (RQD.initClaimCard called once by main.js at boot):
     esc, money, formatDate — function declarations in main, safe by reference.
     getIsAdmin — closure-getter, NOT a bare reference. isAdmin is a `var` in main
       that mutates after Auth.getSession() resolves, so a bare reference captures
       false at init time and the whole card silently disappears for anyone who
       authenticates after boot. Same trap documented in dashboard-quote-row.js.
   ============================================ */
(function () {
  var esc, money, formatDate, getIsAdmin;

  window.RQD = window.RQD || {};
  window.RQD.initClaimCard = function (deps) {
    esc = deps.esc;
    money = deps.money;
    formatDate = deps.formatDate;
    getIsAdmin = deps.getIsAdmin;
  };

  /* Who we chase, by reason. Mirrors REPLACEMENT_REASONS in the worker's
     lib/replacement-order.js — if you add a reason there, add it here. */
  var REASON_LABELS = {
    'carrier-damage': 'Damaged in transit',
    'vendor-defect': 'Vendor defect / DOA',
    'wrong-item': 'Wrong item shipped',
    'warranty': 'Warranty replacement',
    'goodwill': 'Goodwill / our error'
  };

  var CLAIM_STATUSES = [
    { key: 'not-filed', label: 'Not filed yet' },
    { key: 'filed', label: 'Filed' },
    { key: 'in-review', label: 'In review' },
    { key: 'approved', label: 'Approved' },
    { key: 'denied', label: 'Denied' },
    { key: 'paid', label: 'Paid' }
  ];

  function field(ref, name, label, value, type, placeholder) {
    return '<div class="rqd-detail__row rqd-claim-row">' +
      '<span>' + esc(label) + '</span>' +
      '<span><input class="rqd-claim-input" type="' + (type || 'text') + '" ' +
        'data-claim-field="' + esc(name) + '" data-ref="' + esc(ref) + '" ' +
        'value="' + esc(value == null ? '' : String(value)) + '" ' +
        (placeholder ? 'placeholder="' + esc(placeholder) + '" ' : '') +
        '></span>' +
      '</div>';
  }

  /**
   * The claim panel, rendered on a REPLACEMENT order's detail row.
   * Returns '' for anything that isn't a replacement, for non-admins, and for
   * a goodwill replacement (we chose to eat that one — there is nobody to chase,
   * and an empty claim form would imply otherwise).
   */
  function renderClaimCard(q) {
    if (!q || !q.isReplacement) return '';
    if (!getIsAdmin()) return '';

    var claim = q.claim || {};
    var counterparty = claim.counterparty || '';
    var ref = q.reference;
    var reasonLabel = REASON_LABELS[q.replacementReason] || q.replacementReason || '—';

    var html = '<div class="rqd-detail__card rqd-claim-card">';
    /* ⛔ `rqd-detail__section-title`, NOT `rqd-detail__card-title` (Ryan 2026-08-22).
       The latter does not exist anywhere in dashboard.css, so this header rendered as
       unstyled body text next to Sold To and Financials — no uppercase, no letter-spacing,
       no 2px underline, wrong size and colour. It looked like a bespoke header; it was
       simply a class name that matched no rule. Sharing the real class means this heading
       also inherits every per-status palette override (SOLD-green, LOST-red, the prospect
       ramps) for free, which a hand-copied set of values never would. */
    html += '<div class="rqd-detail__section-title">Recovery claim</div>';
    html += '<div class="rqd-detail__row"><span>Reason</span><span>' + esc(reasonLabel) + '</span></div>';
    /* The parent ref carried no class, so it fell through to the browser's default link
       colour — which is not any colour this dashboard uses, and is close to unreadable on
       the dark theme. `rqd-detail__linked-stub-link` is the house treatment for exactly
       this (the "See JW-1129-F for full deal financials" jump on a balance child), and it
       already carries its own dark-mode and linked-card overrides. Reused, not re-tinted. */
    html += '<div class="rqd-detail__row"><span>Replacing</span><span>' +
      (q.replacementParentRef
        ? '<a class="rqd-detail__linked-stub-link" href="#" data-jump-to-parent="' + esc(q.replacementParentRef) + '">' + esc(q.replacementParentRef) + '</a>'
        : '—') + '</span></div>';

    if (counterparty === 'none') {
      html += '<p class="rqd-claim-note">No claim is being tracked — this replacement was logged as our cost to absorb. ' +
        'The goods still get re-shipped and the supplier PO still gets cut; there is simply nobody to invoice for it.</p>';
      html += '</div>';
      return html;
    }

    html += '<div class="rqd-detail__row"><span>Chasing</span><span>' +
      esc(counterparty === 'carrier' ? 'The carrier' : counterparty === 'vendor' ? 'The vendor' : '—') + '</span></div>';

    html += field(ref, 'carrier', counterparty === 'carrier' ? 'Carrier' : 'Vendor', claim.carrier, 'text', 'e.g. XPO, Old Dominion');
    html += field(ref, 'claimNumber', 'Claim #', claim.claimNumber, 'text', 'their reference');
    html += field(ref, 'filedDate', 'Filed', claim.filedDate, 'date');
    html += field(ref, 'amountClaimed', 'Amount claimed', claim.amountClaimed, 'number', '0.00');

    html += '<div class="rqd-detail__row rqd-claim-row"><span>Status</span><span>' +
      '<select class="rqd-claim-input" data-claim-field="status" data-ref="' + esc(ref) + '">';
    for (var i = 0; i < CLAIM_STATUSES.length; i++) {
      var st = CLAIM_STATUSES[i];
      html += '<option value="' + esc(st.key) + '"' +
        ((claim.status || 'not-filed') === st.key ? ' selected' : '') + '>' + esc(st.label) + '</option>';
    }
    html += '</select></span></div>';

    html += field(ref, 'amountRecovered', 'Amount recovered', claim.amountRecovered, 'number', '0.00');
    html += field(ref, 'recoveredDate', 'Recovered', claim.recoveredDate, 'date');

    html += '<div class="rqd-detail__row rqd-claim-row"><span>Notes</span><span>' +
      '<input class="rqd-claim-input" type="text" data-claim-field="notes" data-ref="' + esc(ref) + '" ' +
      'value="' + esc(claim.notes || '') + '" placeholder="adjuster, inspection date, next chase"></span></div>';

    /* The shortfall is the number that matters and nobody should have to work it
       out by hand: it is what this incident actually cost the company once the
       carrier has paid whatever they are going to pay. */
    var claimed = parseFloat(claim.amountClaimed) || 0;
    var recovered = parseFloat(claim.amountRecovered) || 0;
    if (claimed > 0) {
      var short = claimed - recovered;
      var cls = short <= 0 ? ' rqd-detail__row--green' : ' rqd-detail__row--red';
      html += '<div class="rqd-detail__row' + cls + '" title="What is still outstanding on this claim. Commission is unaffected either way.">' +
        '<span>Outstanding</span><span>' + money(short) + '</span></div>';
    }

    html += '<div class="rqd-claim-actions">' +
      '<button type="button" class="rqd-claim-save" data-ref="' + esc(ref) + '">Save claim</button>' +
      '<span class="rqd-claim-saved" data-ref="' + esc(ref) + '" aria-live="polite"></span>' +
      '</div>';
    html += '<p class="rqd-claim-note">Recovering this money does not change anyone’s commission, and neither does failing to. ' +
      'The original order’s payout was settled on what it sold for.</p>';
    html += '</div>';
    return html;
  }

  /**
   * The rep-facing strip on the PARENT deal: "a replacement is in flight".
   * Deliberately carries NO claim data — see the header. `_replacementChildren`
   * is stamped onto the parent row by renderTable when it splits replacements out.
   */
  function renderReplacementStrip(q) {
    if (!q || !Array.isArray(q._replacementChildren) || !q._replacementChildren.length) return '';
    var parts = q._replacementChildren.map(function (r) {
      var reason = REASON_LABELS[r.replacementReason] || 'Replacement';
      var n = r.replacementCandidateCount || 0;
      var bits = [esc(reason)];
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-22) — ⛔ THIS STRIP WAS ALWAYS WRONG.
         It read `r.shippedAt` and `r.poSent`, and NOTHING in the worker or the theme has ever
         written either field. Both branches were dead, so the rep-facing line on the parent deal
         said "items being selected" or "being set up" forever, no matter how far along the
         replacement actually was — the exact question a rep is trying to answer for a customer
         who is waiting on goods.

         Now driven by the same derived stage as the pill and the fulfilment bar, so all three
         say one thing. Still carries NO claim data: the claim is the admin's separate clock and
         a rep reading it to answer "when does my customer get it" tells them the wrong thing. */
      if (r.replacementStage === 'shipped') bits.push('shipped');
      else if (r.replacementStage === 'ordered') bits.push('PO sent — on order');
      else if (n) bits.push('items being selected');
      else bits.push('being set up');
      return '<a href="#" data-jump-to-parent="' + esc(r.reference) + '">' + esc(r.reference) + '</a> — ' + bits.join(' · ');
    });
    return '<div class="rqd-detail__linked-banner rqd-replacement-banner">' +
      '<span class="rqd-detail__linked-banner-icon">↩</span> ' +
      (parts.length === 1 ? 'A replacement is in progress: ' : 'Replacements in progress: ') +
      parts.join(' &nbsp;·&nbsp; ') +
      ' <span class="rqd-replacement-banner__note">No charge to the customer.</span>' +
      '</div>';
  }

  /* ============================================================
     THE GOODS CLOCK (Ryan 2026-08-22)

     ⛔ WHY A REPLACEMENT COULD NOT BE ORDERED OR TRACKED. Every fulfilment surface on this
     board gates on `q.lifecycleStatus === 'sold'`, and a replacement is deliberately never
     sold — so the Purchasing card (the ONLY place the Generate PO button exists), the Tracking
     card, the fulfilment bar and the rep summary all skipped it. Ryan: "the big point of this
     tool … is to allow us to order replacement orders for customers prior to the claim being
     completed". The worker was always willing — /generate-po has no sold check and builds from
     cost — there was simply no button that reached it.

     ⛔ ONE PREDICATE, NOT FOUR COPIES. Four call sites need this exact question, and this file
     loads before all of them (see the script order in rep-quote-dashboard.liquid).

     ⛔ AND IT IS DELIBERATELY NARROW. This means "the goods are committed, so ordering and
     shipping apply" — NOT "treat it like a sale". The AR/AP card, invoicing, payments and
     commission stay shut on a $0 replacement, which is why `isSoldOrClosed` was NOT widened
     globally. Money surfaces keep asking the old question. */
  window.RQD.dealGoodsAreCommitted = function (q) {
    if (!q) return false;
    return q.lifecycleStatus === 'sold' || !!q.isReplacement;
  };

  /* Stage labels, mirroring lib/replacement-order.js REPLACEMENT_STAGE_LABELS. Theme JS cannot
     import from the worker; a test asserts the two agree. All nine characters — `.rqd-status`
     is a fixed 94px nowrap pill and "↩ REPLACEMENT" (thirteen) is what overflowed it. */
  window.RQD.REPLACEMENT_STAGE_LABELS = {
    'awaiting-items': '↩ REPLACE',
    'awaiting-po': '↩ REPLACE',
    ordered: '↩ ORDERED',
    shipped: '↩ SHIPPED'
  };

  window.RQD.renderClaimCard = renderClaimCard;
  window.RQD.renderReplacementStrip = renderReplacementStrip;
})();
